import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import BlockIcon from "@mui/icons-material/BlockOutlined";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { CallContact, ResolvedCallPolicy } from "@shared/types/foyer";
import { foyerApi, type RoomTypeRecord } from "../http/foyer";
import CallContactDialog from "./CallContactDialog";
import DangerConfirmDialog from "./DangerConfirmDialog";
import TableSkeleton from "./TableSkeleton";
import { describeError, useToast } from "./toast";
import { Trans, useTranslation } from "react-i18next";
import { PermissionGate, usePermission } from "../auth/access";
import { errorText } from "../utils/apiError";
import { useFormat, type Formatters } from "../i18n/format";
import { translateKey, type Translate } from "../i18n/translate";

/**
 * Care → Video calls · Directory (VC-10).
 *
 * ONE list with TWO direction checkboxes, rendered in-row. That is the whole
 * design decision: the same five people appear in both directions, and two
 * separate lists drift - which is exactly how "the relative removed from the
 * visitor list last month can still ring the room" happens. Keeping both flags
 * visible in the table means "can Reception ring rooms?" is answered by looking,
 * not by opening five dialogs.
 *
 * Family contacts are deliberately absent: they hang off the RESIDENT
 * (`scope: "person"`, VC-11), never off a room or the premise, so they are
 * authored in the admission flow and filtered out here even if the API returns
 * them.
 *
 * The table never shows a phone number. The list view carries masks only; the
 * real values are a separate, audited disclosure, so a page load can't quietly
 * become fifty PHI reads.
 */

interface Props {
  premiseId: string;
  /** Premise timezone - prefilled into a contact's quiet hours. */
  premiseTimezone?: string;
}

const NO_VALUE = "-";
const ENDPOINT_SEPARATOR = " · ";

/** "premise" → every room; "roomType" → one ward, named where we can. */
function scopeLabel(
  t: Translate,
  c: CallContact,
  roomTypes: RoomTypeRecord[]
): string {
  if (c.scope === "premise") return t("care:callDirectoryEditor.allUnits");
  const rt = roomTypes.find((r) => r.id === c.roomTypeId);
  return rt
    ? rt.name
    : (c.roomTypeId ?? t("care:callDirectoryEditor.unitType"));
}

/** Masked endpoints, e.g. "+46 70 ***** 67 · an***@example.com". */
function endpointSummary(t: Translate, c: CallContact): string {
  const parts: string[] = [];
  if (c.endpointHints?.phone) parts.push(c.endpointHints.phone);
  if (c.endpointHints?.email) parts.push(c.endpointHints.email);
  if (c.endpointKinds?.includes("foyerUser")) {
    parts.push(t("care:callDirectoryEditor.foyerUser"));
  }
  return parts.join(ENDPOINT_SEPARATOR) || NO_VALUE;
}

/** Human window, or "Always" - the common case, and it should read as calm. */
function windowLabel(t: Translate, fmt: Formatters, c: CallContact): string {
  if (c.activeFrom && c.activeUntil) {
    return t("care:callDirectoryEditor.window.range", {
      from: fmt.date(c.activeFrom),
      to: fmt.date(c.activeUntil),
    });
  }
  if (c.activeFrom) {
    return t("care:callDirectoryEditor.window.from", {
      date: fmt.date(c.activeFrom),
    });
  }
  if (c.activeUntil) {
    return t("care:callDirectoryEditor.window.until", {
      date: fmt.date(c.activeUntil),
    });
  }
  return t("care:callDirectoryEditor.window.always");
}

export default function CallDirectoryEditor({
  premiseId,
  premiseTimezone,
}: Props) {
  const toast = useToast();
  const { t } = useTranslation(["care", "common"]);
  const fmt = useFormat();
  const mayEdit = usePermission("video-calls", "edit", { premiseId });
  const [contacts, setContacts] = useState<CallContact[] | null>(null);
  const [roomTypes, setRoomTypes] = useState<RoomTypeRecord[]>([]);
  const [policy, setPolicy] = useState<ResolvedCallPolicy | null>(null);
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyRow, setBusyRow] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CallContact | null>(null);
  const [revoking, setRevoking] = useState<CallContact | null>(null);
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const rows = await foyerApi.listCallContacts(premiseId, {
        includeRevoked,
      });
      // Person-scoped rows are a resident's family; they belong to the
      // admission flow, not to the premise directory.
      setContacts(rows.filter((c) => c.scope !== "person"));
      setError(null);
    } catch (err) {
      setContacts([]);
      setError(errorText(err, t));
    }
  }, [premiseId, includeRevoked, t]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    foyerApi
      .listRoomTypes()
      .then(setRoomTypes)
      .catch(() => setRoomTypes([]));
  }, []);

  useEffect(() => {
    foyerApi
      .getCallPolicy(premiseId)
      .then(setPolicy)
      .catch(() => setPolicy(null));
  }, [premiseId]);

  const sorted = useMemo(() => {
    const rows = contacts ?? [];
    return [...rows].sort(
      (a, b) =>
        Number(!!a.revokedAt) - Number(!!b.revokedAt) ||
        a.priority - b.priority ||
        a.displayName.localeCompare(b.displayName)
    );
  }, [contacts]);

  /** Flip one direction flag straight from the row - the point of the table. */
  const toggleFlag = async (
    c: CallContact,
    field: "callableFromRoom" | "canCallRoom",
    next: boolean
  ) => {
    setBusyRow(c.id);
    setError(null);
    try {
      await foyerApi.updateCallContact(premiseId, c.id, { [field]: next });
      const name = c.displayName;
      toast.success(
        field === "callableFromRoom"
          ? next
            ? t("callDirectoryEditor.canBeCalled", { name })
            : t("callDirectoryEditor.cannotBeCalled", { name })
          : next
            ? t("callDirectoryEditor.canCall", { name })
            : t("callDirectoryEditor.cannotCall", { name })
      );
      await refresh();
    } catch (err) {
      setError(errorText(err, t));
      toast.error(
        describeError(t, "updateContact", err, { name: c.displayName })
      );
    } finally {
      setBusyRow(null);
    }
  };

  const confirmRevoke = async () => {
    if (!revoking) return;
    setRevokeBusy(true);
    setRevokeError(null);
    try {
      await foyerApi.revokeCallContact(premiseId, revoking.id);
      setRevoking(null);
      toast.success(
        t("callDirectoryEditor.revoked", { name: revoking.displayName })
      );
      await refresh();
    } catch (err) {
      setRevokeError(errorText(err, t));
      toast.error(
        describeError(t, "revokeContact", err, { name: revoking.displayName })
      );
    } finally {
      setRevokeBusy(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (c: CallContact) => {
    setEditing(c);
    setDialogOpen(true);
  };

  return (
    <Stack spacing={3} data-testid="call-directory">
      <Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 760 }}
        >
          <Trans
            t={t}
            i18nKey="callDirectoryEditor.intro"
            components={{ strong: <strong /> }}
          />
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {policy && !policy.enabled && (
        <Alert severity="warning" data-testid="calls-disabled-warning">
          <Trans
            t={t}
            i18nKey="callDirectoryEditor.callsDisabled"
            components={{ strong: <strong /> }}
          />
        </Alert>
      )}

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={includeRevoked}
              onChange={(e) => setIncludeRevoked(e.target.checked)}
              data-testid="include-revoked"
            />
          }
          label={t("callDirectoryEditor.showRevoked")}
        />
        <PermissionGate
          resource="video-calls"
          level="edit"
          place={{ premiseId }}
        >
          <Button
            variant="contained"
            size="small"
            startIcon={<PersonAddIcon />}
            onClick={openCreate}
            data-testid="add-contact"
          >
            {t("callDirectoryEditor.addContact")}
          </Button>
        </PermissionGate>
      </Stack>

      {contacts === null ? (
        <TableSkeleton rows={3} />
      ) : sorted.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("callDirectoryEditor.empty")}
        </Typography>
      ) : (
        <Table size="small" data-testid="contacts-table">
          <TableHead>
            <TableRow>
              <TableCell>{t("callDirectoryEditor.columns.contact")}</TableCell>
              <TableCell>
                {t("callDirectoryEditor.columns.appliesTo")}
              </TableCell>
              <TableCell align="center">
                <Tooltip title={t("callDirectoryEditor.columns.callableHint")}>
                  <span>
                    {t("callDirectoryEditor.columns.callableFromUnits")}
                  </span>
                </Tooltip>
              </TableCell>
              <TableCell align="center">
                <Tooltip title={t("callDirectoryEditor.columns.canRingHint")}>
                  <span>{t("callDirectoryEditor.columns.canRingUnits")}</span>
                </Tooltip>
              </TableCell>
              <TableCell>
                {t("callDirectoryEditor.columns.reachedVia")}
              </TableCell>
              <TableCell>{t("callDirectoryEditor.columns.active")}</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((c) => {
              const revoked = !!c.revokedAt;
              return (
                <TableRow
                  key={c.id}
                  data-testid={`contact-row-${c.id}`}
                  sx={revoked ? { opacity: 0.55 } : undefined}
                >
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar
                        src={c.avatarAssetId ?? undefined}
                        sx={{ width: 32, height: 32 }}
                      >
                        {c.displayName.slice(0, 1).toUpperCase()}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                        >
                          <Typography
                            variant="body2"
                            fontWeight="fontWeightMedium"
                          >
                            {c.displayName}
                          </Typography>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={translateKey(
                              t,
                              `care:callDirectoryEditor.kind.${c.kind}`,
                              { defaultValue: c.kind }
                            )}
                          />
                          {c.autoAnswer && (
                            <Tooltip
                              title={t("callDirectoryEditor.autoAnswerHint")}
                            >
                              <Chip
                                size="small"
                                color="warning"
                                label={t("callDirectoryEditor.autoAnswer")}
                              />
                            </Tooltip>
                          )}
                          {revoked && (
                            <Chip
                              size="small"
                              color="error"
                              label={t("callDirectoryEditor.revokedChip")}
                            />
                          )}
                        </Stack>
                        {c.relation && (
                          <Typography variant="caption" color="text.secondary">
                            {c.relation}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {scopeLabel(t, c, roomTypes)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      size="small"
                      checked={c.callableFromRoom}
                      disabled={revoked || busyRow === c.id || !mayEdit}
                      onChange={(e) =>
                        void toggleFlag(c, "callableFromRoom", e.target.checked)
                      }
                      inputProps={
                        {
                          "aria-label": t("callDirectoryEditor.callableAria", {
                            name: c.displayName,
                          }),
                          "data-testid": `callable-${c.id}`,
                        } as React.InputHTMLAttributes<HTMLInputElement>
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      size="small"
                      checked={c.canCallRoom}
                      disabled={revoked || busyRow === c.id || !mayEdit}
                      onChange={(e) =>
                        void toggleFlag(c, "canCallRoom", e.target.checked)
                      }
                      inputProps={
                        {
                          "aria-label": t("callDirectoryEditor.canRingAria", {
                            name: c.displayName,
                          }),
                          "data-testid": `can-ring-${c.id}`,
                        } as React.InputHTMLAttributes<HTMLInputElement>
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {endpointSummary(t, c)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {c.quietHours
                        ? t("callDirectoryEditor.window.quiet", {
                            window: windowLabel(t, fmt, c),
                            from: c.quietHours.from,
                            to: c.quietHours.to,
                          })
                        : windowLabel(t, fmt, c)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="flex-end"
                    >
                      <PermissionGate
                        resource="video-calls"
                        level="edit"
                        place={{ premiseId }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={() => openEdit(c)}
                          disabled={revoked}
                          data-testid={`edit-${c.id}`}
                        >
                          {t("common:buttons.edit")}
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<BlockIcon />}
                          onClick={() => {
                            setRevokeError(null);
                            setRevoking(c);
                          }}
                          disabled={revoked}
                          data-testid={`revoke-${c.id}`}
                        >
                          {t("common:buttons.revoke")}
                        </Button>
                      </PermissionGate>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <CallContactDialog
        open={dialogOpen}
        premiseId={premiseId}
        contact={editing}
        defaultTz={premiseTimezone || "Europe/Stockholm"}
        allowAutoAnswer={policy?.allowAutoAnswer ?? false}
        onClose={() => setDialogOpen(false)}
        onSaved={(msg) => {
          toast.success(msg);
          void refresh();
        }}
      />

      <DangerConfirmDialog
        open={!!revoking}
        title={t("callDirectoryEditor.revokeDialog.title")}
        entityName={revoking?.displayName ?? ""}
        confirmLabel={t("common:buttons.revoke")}
        acknowledgeLabel={t("callDirectoryEditor.revokeDialog.acknowledge")}
        body={
          <Typography variant="body2">
            {t("callDirectoryEditor.revokeDialog.body")}
          </Typography>
        }
        errorMessage={revokeError}
        busy={revokeBusy}
        onConfirm={() => void confirmRevoke()}
        onCancel={() => setRevoking(null)}
      />
    </Stack>
  );
}
